import * as React from "react";
import { makeStyles } from "@material-ui/styles";

import { FormControlLabel, RadioGroup, Radio } from "@material-ui/core";
import classNames from "classnames";
import { CheckCircle } from "@material-ui/icons";
import { FormControlLabelProps } from "@material-ui/core/FormControlLabel";

import { colors, greys, fontFamilyHelvetica } from "@styles/index";
import { CWIDProps } from "@util/cw-id.util";

export interface CwRadioProps {
  value: string;
  ariaLabel: string;
  onChange: (value: string) => void;
  children: JSX.Element[];
  className?: string;
}

const useStyles = makeStyles({
  container: {
    width: "fit-content",
  },
  radio: {
    borderRadius: 10,
    border: `${2.75 / 16}rem solid ${greys.paleGrey}`,
    marginBottom: 8,
    padding: "10px 15px 9px 5px",
    transition: "border 100ms cubic-bezier(0.4, 0, 0.2, 1) 0ms;", // Match MUI fill transition.

    "&.checked": {
      borderColor: colors.blueSky.darker,
    },
  },
});

const radioOptionStyles = makeStyles({
  container: {
    padding: 5,
    fontFamily: fontFamilyHelvetica,
  },
  header: { marginBottom: 4 },
  subHeader: {
    color: greys.slate,
  },
});

export const TwoRowRadioOption = ({
  value,
  header,
  subHeader,
  className,
  control,
  "cw-id": cwId,
}: {
  value: string;
  header: string;
  subHeader: string;
  className?: string;
  control?: React.ReactElement<any>;
} & CWIDProps) => {
  const classes = radioOptionStyles();

  return (
    <FormControlLabel
      control={control || <Radio />}
      className={className}
      value={value}
      cw-id={cwId}
      label={
        <div className={classes.container}>
          <div className={classes.header}>{header}</div>
          <div className={classes.subHeader}>{subHeader}</div>
        </div>
      }
    />
  );
};

export const CwRadio = ({ value, onChange, ariaLabel, children, className }: CwRadioProps) => {
  const classes = useStyles();

  return (
    <RadioGroup
      value={value}
      aria-label={ariaLabel}
      name={ariaLabel}
      className={classNames([classes.container, className])}
      onChange={(e) => onChange(e.target.value)}
    >
      {React.Children.map(children, (child) => {
        if (React.isValidElement(child)) {
          return React.cloneElement(child, {
            control: (
              <Radio
                color="primary"
                checkedIcon={<CheckCircle htmlColor={colors.blueSky.darker} />}
              />
            ),
            className: classNames([
              classes.radio,
              (child?.props as Pick<CwRadioProps, "value">)?.value === value && "checked",
            ]),
          } as FormControlLabelProps);
        }
        return child;
      })}
    </RadioGroup>
  );
};
